import { ConversationMessageMediaItemType } from 'lib/enum/conversation-message-media-item-type.enum';
import { ConversationMessageType } from 'lib/enum/conversation-message-type.enum';
import { BirdConfigurationError } from 'lib/enum/bird-configuration-error.enum';
import * as Yup from 'yup';
import { TalentConversationLocation } from 'lib/enum/lead-conversation-location';
import { ChannelType } from 'lib/enum/message-channel.enum';
import { FormValues } from './types';
import React from 'react';
import { MessagingChannel } from 'services';

const CAPTION_MAX_LENGTH = 500;
const SMS_MAX_LENGTH = 603;
const WHATSAPP_MAX_LENGTH = 4096;
export const DETAILS_COLUMN_ID = 'leads-inbox-column-details';
export const MAXIMUM_SMS_LENGTH_ERROR_MESSAGE = `Maximum message length for SMS channel is ${SMS_MAX_LENGTH}`;

export const MAXIMUM_WHATSAPP_LENGTH_ERROR_MESSAGE = `Maximum message length for WhatsApp channel is ${WHATSAPP_MAX_LENGTH}`;

export const getIsMaximumSmsLengthErrorMessage = (errorMessage: string): boolean =>
    errorMessage === MAXIMUM_SMS_LENGTH_ERROR_MESSAGE;

export const getIsMaximumWhatsappLengthErrorMessage = (errorMessage: string): boolean =>
    errorMessage === MAXIMUM_WHATSAPP_LENGTH_ERROR_MESSAGE;

export const hasOnlyWebChatChannels = (channels: MessagingChannel[]): boolean =>
    channels.length > 0 && channels.every(channel => channel.type === ChannelType.WebChat);

export const getInboxValidationSchema = (availableChannels: MessagingChannel[]): Yup.AnySchema =>
    Yup.object({
        channelId: Yup.string().required(),
        text: Yup.string()
            .when('templateId', (templateId, schema: Yup.StringSchema) => {
                if (!!templateId) {
                    return schema;
                }
                return schema.when('mediaElements', (mediaElements, schema) => {
                    if (mediaElements.length) {
                        return schema;
                    }
                    return schema.trim().required();
                });
            })
            .when('channelId', (channelId, schema: Yup.StringSchema) => {
                const currentChannel = availableChannels.find(channel => channel.id === channelId);

                switch (currentChannel?.type) {
                    case ChannelType.Sms:
                        return schema.trim().max(SMS_MAX_LENGTH, MAXIMUM_SMS_LENGTH_ERROR_MESSAGE).min(1);
                    case ChannelType.Whatsapp:
                        return schema.trim().max(WHATSAPP_MAX_LENGTH, MAXIMUM_WHATSAPP_LENGTH_ERROR_MESSAGE).min(1);
                }
                return schema;
            }),
        subject: Yup.string().when('channelId', (channelId, schema: Yup.StringSchema) => {
            const currentChannel = availableChannels.find(channel => channel.id === channelId);
            if (currentChannel?.type === ChannelType.Email) {
                return schema.trim().required();
            }
            return schema;
        }),
        mediaElements: Yup.array().of(
            Yup.object().shape({
                type: Yup.string().oneOf(Object.values(ConversationMessageMediaItemType)),
                item: Yup.string().required(),
                caption: Yup.string()
                    .trim()
                    .max(CAPTION_MAX_LENGTH, `Caption must be at most ${CAPTION_MAX_LENGTH} characters`),
            }),
        ),
        templateId: Yup.string().when('messageType', {
            is: ConversationMessageType.WhatsappTemplateSent,
            then: Yup.string().required().trim(),
        }),
    });

export const WHATSAPP_HOURS_TIME_WINDOW_FOR_RESPONSE = 24;

export const getConversationListErrorMessage = (birdConfigurationError: BirdConfigurationError): string => {
    switch (birdConfigurationError) {
        case BirdConfigurationError.NoChannelSet:
            return 'No channel configured';
        case BirdConfigurationError.NotConfigured:
            return 'Inbox not configured';
    }
};

export const getConfigurationErrorTitle = (birdConfigurationError: BirdConfigurationError): string => {
    switch (birdConfigurationError) {
        case BirdConfigurationError.NoChannelSet:
            return 'Inbox channels not configured';
        case BirdConfigurationError.NotConfigured:
            return 'Message inbox not configured';
    }
};

export const getConfigurationErrorDescription = (birdConfigurationError: BirdConfigurationError): [string, string] => {
    switch (birdConfigurationError) {
        case BirdConfigurationError.NoChannelSet:
            return [
                'Your inbox channels are not configured yet. Please contact',
                'MrWork support to set up your inbox and get started!',
            ];
        case BirdConfigurationError.NotConfigured:
            return [
                'Your inbox is not configured yet. Please contact',
                'MrWork support to set up your inbox and get started!',
            ];
    }
};

export const INITIAL_VALUES: FormValues = {
    channelId: '',
    text: '',
    htmlText: '',
    subject: '',
    mediaElements: [],
    templateId: '',
    parameters: {},
};

export const getInitialInboxValues = (initialChannelId?: string): FormValues => {
    return {
        ...INITIAL_VALUES,
        channelId: initialChannelId || '',
    };
};

export const DEFAULT_PAGE_TO_DISPLAY = 1;
export const DEFAULT_ITEMS_PER_PAGE = 16;
export const CONVERSATION_LIST_ITEM_ELEMENT_SIZE = 88;

export enum LeadsConversationsFilterName {
    Assignee = 'assignee[]',
    Status = 'status[]',
    Search = 'search',
    Talent = 'talent',
    IncludeEmptyConversations = 'includeEmptyConversations',
    EmptyConversations = 'emptyConversations',
    Location = 'location',
    Unread = 'unread',
    Page = 'page',
    ItemsPerPage = 'itemsPerPage',
    Pagination = 'pagination',
    ContentTags = 'contentTags[]',
    ContentTag = 'contentTag',
    Talentpool = 'talentpool',
    ExpiringSoonOnWhatsapp = 'expiringSoonOnWhatsapp',
    CurrentAgentSessionInActiveMode = 'currentAgentSessionInActiveMode',
}

export enum LeadsConversationsOrderName {
    LatestManualMessageAt = 'order[talent.touchpoints.latestManualMessageAt]',
    LatestMessageAt = 'order[talent.touchpoints.latestMessageAt]',
    LatestSentMessageAt = 'order[talent.touchpoints.latestSentMessageAt]',
    LatestWhatsappSentMessageAt = 'order[talent.touchpoints.latestWhatsappSentMessageAt]',
    LatestEmailSentMessageAt = 'order[talent.touchpoints.latestEmailSentMessageAt]',
    LatestSmsSentMessageAt = 'order[talent.touchpoints.latestSmsSentMessageAt]',
    LatestWhatsappReceivedMessageAt = 'order[talent.touchpoints.latestWhatsappReceivedMessageAt]',
    Id = 'order[id]',
}

export enum LeadsConversationsExistsName {
    LatestSentMessageAt = 'exists[talent.touchpoints.latestSentMessageAt]',
    LatestWhatsappSentMessageAt = 'exists[talent.touchpoints.latestWhatsappSentMessageAt]',
    LatestEmailSentMessageAt = 'exists[talent.touchpoints.latestEmailSentMessageAt]',
    LatestSmsSentMessageAt = 'exists[talent.touchpoints.latestSmsSentMessageAt]',
    LatestManualMessageAt = 'exists[talent.touchpoints.latestManualMessageAt]',
}

export enum OrderDirection {
    ASC = 'asc',
    DESC = 'desc',
}

export enum ConversationHeaderModals {
    ChangeTalentpool,
    ChangeStatus,
    ChangeStatusDirectly,
}

export interface LeadsConversationsFilters {
    [LeadsConversationsFilterName.Assignee]: string | string[];
    [LeadsConversationsFilterName.Status]: string;
    [LeadsConversationsFilterName.Search]: string;
    [LeadsConversationsFilterName.Talent]: string;
    [LeadsConversationsFilterName.IncludeEmptyConversations]: boolean | void;
    [LeadsConversationsFilterName.EmptyConversations]: boolean | void;
    [LeadsConversationsFilterName.Location]: TalentConversationLocation | void;
    [LeadsConversationsFilterName.Unread]: boolean | void;
    [LeadsConversationsFilterName.Page]: number;
    [LeadsConversationsFilterName.ItemsPerPage]: number;
    [LeadsConversationsFilterName.Pagination]: boolean;
    [LeadsConversationsFilterName.ContentTags]: string;
    [LeadsConversationsFilterName.ContentTag]: string;
    [LeadsConversationsFilterName.Talentpool]: string;
    [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: boolean | void;
    [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: boolean | void;
    [LeadsConversationsExistsName.LatestSentMessageAt]: string;
    [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: string;
    [LeadsConversationsExistsName.LatestEmailSentMessageAt]: string;
    [LeadsConversationsExistsName.LatestSmsSentMessageAt]: string;
    [LeadsConversationsExistsName.LatestManualMessageAt]: string;
}

export const BrowserBarUrlParamNames: Record<
    Exclude<LeadsConversationsFilterName, LeadsConversationsFilterName.Pagination>,
    string
> &
    Record<LeadsConversationsOrderName, string> &
    Record<LeadsConversationsExistsName, string> = {
    [LeadsConversationsFilterName.Assignee]: 'assignee[]',
    [LeadsConversationsFilterName.Status]: 'status[]',
    [LeadsConversationsFilterName.Search]: 'search',
    [LeadsConversationsFilterName.Talent]: 'talent',
    [LeadsConversationsFilterName.IncludeEmptyConversations]: 'includeEmptyConversations',
    [LeadsConversationsFilterName.EmptyConversations]: 'emptyConversations',
    [LeadsConversationsFilterName.Location]: 'location',
    [LeadsConversationsFilterName.Unread]: 'unread',
    [LeadsConversationsFilterName.Page]: 'page',
    [LeadsConversationsFilterName.ItemsPerPage]: 'itemsPerPage',
    [LeadsConversationsFilterName.ContentTags]: 'contentTags[]',
    [LeadsConversationsFilterName.ContentTag]: 'contentTag',
    [LeadsConversationsFilterName.Talentpool]: 'talentpool',
    [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: 'expiringSoonOnWhatsapp',
    [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: 'currentAgentSessionInActiveMode',
    [LeadsConversationsOrderName.LatestManualMessageAt]: LeadsConversationsOrderName.LatestManualMessageAt,
    [LeadsConversationsOrderName.LatestMessageAt]: LeadsConversationsOrderName.LatestMessageAt,
    [LeadsConversationsOrderName.LatestSentMessageAt]: LeadsConversationsOrderName.LatestSentMessageAt,
    [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: LeadsConversationsOrderName.LatestWhatsappSentMessageAt,
    [LeadsConversationsOrderName.LatestEmailSentMessageAt]: LeadsConversationsOrderName.LatestEmailSentMessageAt,
    [LeadsConversationsOrderName.LatestSmsSentMessageAt]: LeadsConversationsOrderName.LatestSmsSentMessageAt,
    [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]:
        LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt,
    [LeadsConversationsOrderName.Id]: LeadsConversationsOrderName.Id,
    [LeadsConversationsExistsName.LatestSentMessageAt]: LeadsConversationsExistsName.LatestSentMessageAt,
    [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]:
        LeadsConversationsExistsName.LatestWhatsappSentMessageAt,
    [LeadsConversationsExistsName.LatestEmailSentMessageAt]: LeadsConversationsExistsName.LatestEmailSentMessageAt,
    [LeadsConversationsExistsName.LatestSmsSentMessageAt]: LeadsConversationsExistsName.LatestSmsSentMessageAt,
    [LeadsConversationsExistsName.LatestManualMessageAt]: LeadsConversationsExistsName.LatestManualMessageAt,
};

export const EndpointUrlParamNames: Record<LeadsConversationsFilterName, string> &
    Record<LeadsConversationsOrderName, string> &
    Record<LeadsConversationsExistsName, string> = {
    [LeadsConversationsFilterName.Assignee]: 'assignee[]',
    [LeadsConversationsFilterName.Status]: 'talent.status[]',
    [LeadsConversationsFilterName.Search]: 'search',
    [LeadsConversationsFilterName.Talent]: 'talent',
    [LeadsConversationsFilterName.Page]: 'page',
    [LeadsConversationsFilterName.ItemsPerPage]: 'itemsPerPage',
    [LeadsConversationsFilterName.Pagination]: 'pagination',
    [LeadsConversationsFilterName.IncludeEmptyConversations]: 'includeEmptyConversations',
    [LeadsConversationsFilterName.EmptyConversations]: 'emptyConversations',
    [LeadsConversationsFilterName.Location]: 'location',
    [LeadsConversationsFilterName.Unread]: 'unread',
    [LeadsConversationsFilterName.ContentTags]: 'contentTags[]',
    [LeadsConversationsFilterName.ContentTag]: 'contentTags[]',
    [LeadsConversationsFilterName.Talentpool]: 'talentpool',
    [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: 'expiringSoonOnWhatsapp',
    [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: 'currentAgentSessionInActiveMode',
    [LeadsConversationsOrderName.LatestManualMessageAt]: LeadsConversationsOrderName.LatestManualMessageAt,
    [LeadsConversationsOrderName.LatestMessageAt]: LeadsConversationsOrderName.LatestMessageAt,
    [LeadsConversationsOrderName.LatestSentMessageAt]: LeadsConversationsOrderName.LatestSentMessageAt,
    [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: LeadsConversationsOrderName.LatestWhatsappSentMessageAt,
    [LeadsConversationsOrderName.LatestEmailSentMessageAt]: LeadsConversationsOrderName.LatestEmailSentMessageAt,
    [LeadsConversationsOrderName.LatestSmsSentMessageAt]: LeadsConversationsOrderName.LatestSmsSentMessageAt,
    [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]:
        LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt,
    [LeadsConversationsOrderName.Id]: LeadsConversationsOrderName.Id,
    [LeadsConversationsExistsName.LatestSentMessageAt]: LeadsConversationsExistsName.LatestSentMessageAt,
    [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]:
        LeadsConversationsExistsName.LatestWhatsappSentMessageAt,
    [LeadsConversationsExistsName.LatestEmailSentMessageAt]: LeadsConversationsExistsName.LatestEmailSentMessageAt,
    [LeadsConversationsExistsName.LatestSmsSentMessageAt]: LeadsConversationsExistsName.LatestSmsSentMessageAt,
    [LeadsConversationsExistsName.LatestManualMessageAt]: LeadsConversationsExistsName.LatestManualMessageAt,
};

export const statusFilterValueToStatusEndpointFilterValue = (value: string): string => value;

export const getPageParamValueFromHistory = (historyLocationSearch: string): number => {
    return +(
        new URLSearchParams(historyLocationSearch).get(LeadsConversationsFilterName.ItemsPerPage) ||
        DEFAULT_ITEMS_PER_PAGE
    );
};

export enum TalentsInboxSidebarModeType {
    TalentsProfile = 'TalentsProfile',
    Notes = 'Notes',
}

export type TalentsInboxSidebarMode =
    | {
          type: TalentsInboxSidebarModeType.TalentsProfile;
          talentsProfileId: string;
      }
    | {
          type: TalentsInboxSidebarModeType.Notes;
          talentId: string;
      };

export enum TalentInboxBulkActionModal {
    ChangeTalentpool = 'ChangeTalentpool',
    SendToATS = 'SendToAts',
    StartJourney = 'StartJourney',
    SendWaTemplateMessages = 'SendWaTemplateMessages',
}

export const SMALL_WIDTH_BREAKPOINT = 1144;

export const MEDIUM_WIDTH_BREAKPOINT = 1690;

export type FilterColumnItem = {
    value: string;
    label: string;
    unreadCount: number;
    avatar?: React.JSX.Element;
    isCopyOfPinned?: boolean;
};
